import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, TrendingUp, Building2, X } from "lucide-react";
import { apiRequest } from "@/api/client";

const SCORE_COLOR = (avg) => {
  if (avg === 0) return "bg-slate-50 text-slate-300";
  if (avg < 1.5) return "bg-emerald-100 text-emerald-900 hover:bg-emerald-200";
  if (avg < 2.5) return "bg-amber-100 text-amber-900 hover:bg-amber-200";
  if (avg < 3.5) return "bg-orange-200 text-orange-900 hover:bg-orange-300";
  return "bg-rose-300 text-rose-950 hover:bg-rose-400";
};

export default function Analytics() {
  const { t } = useTranslation();
  const [heatmap, setHeatmap] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [drillDown, setDrillDown] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [h, r] = await Promise.all([
        apiRequest(`/analytics/ateco-heatmap${qs}`),
        apiRequest("/analytics/retention").catch(() => null),
      ]);
      setHeatmap(h);
      setRetention(r);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dateFrom, dateTo]);

  const openDrillDown = async (ateco, category) => {
    setDrillLoading(true);
    try {
      const params = new URLSearchParams({ ateco, category });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const data = await apiRequest(`/analytics/ateco-heatmap/drilldown?${params.toString()}`);
      setDrillDown(data);
    } catch (e) { setError(e.message); }
    finally { setDrillLoading(false); }
  };

  if (loading && !heatmap) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="analytics-loading">Caricamento analytics…</div>;
  if (error && !heatmap) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-800" data-testid="analytics-error">{error}</div>;

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Analytics multi-tenant</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="analytics-title">{t("analytics.title")}</h1>
        <p className="mt-2 text-slate-600">{t("analytics.subtitle")} Clicca su una cella per vedere le aziende associate.</p>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
          <span className="flex items-center gap-2"><Building2 className="h-4 w-4" data-testid="analytics-icon-companies" /> {heatmap.totals.companies} {t("analytics.companies")}</span>
          <span className="flex items-center gap-2"><Activity className="h-4 w-4" /> {heatmap.totals.assessments} {t("analytics.assessments")}</span>
          <span className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> {heatmap.totals.ateco_count} codici · {heatmap.totals.categories_count} categorie</span>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4" data-testid="analytics-filters">
          <div>
            <label className="block text-xs font-semibold text-slate-500">Da</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid="analytics-date-from" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500">A</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid="analytics-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50" data-testid="analytics-clear-filters">Pulisci filtri</button>
          )}
        </div>
      </section>

      {heatmap.matrix.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="analytics-heatmap-empty">{t("analytics.empty")}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="analytics-heatmap-wrapper">
          <table className="min-w-full text-sm" data-testid="analytics-heatmap-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">ATECO</th>
                {heatmap.categories.map((cat) => (
                  <th key={cat} className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500" title={cat}>
                    <span className="block max-w-[80px] truncate">{cat}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmap.matrix.map((row) => (
                <tr key={row.ateco} className="border-t border-slate-100" data-testid={`heatmap-row-${row.ateco}`}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 font-mono text-sm font-semibold text-slate-700">{row.ateco}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.category} className="p-1" data-testid={`heatmap-cell-${row.ateco}-${cell.category}`}>
                      <button
                        type="button"
                        disabled={cell.count === 0}
                        onClick={() => openDrillDown(row.ateco, cell.category)}
                        className={`flex h-12 w-full flex-col items-center justify-center rounded text-xs font-semibold transition ${SCORE_COLOR(cell.average_score)} ${cell.count === 0 ? "cursor-default" : "cursor-pointer"}`}
                        title={`${cell.count} valutazioni · ${cell.company_count} aziende · max ${cell.max_score}/4 — click per drill-down`}
                        data-testid={`heatmap-cell-button-${row.ateco}-${cell.category}`}
                      >
                        {cell.count > 0 ? (
                          <>
                            <span>{cell.average_score}</span>
                            <span className="text-[9px] opacity-70">n={cell.count}/{cell.company_count}</span>
                          </>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <span className="font-semibold">Legenda:</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-100" /> Basso (1.0–1.5)</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-100" /> Moderato (1.5–2.5)</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-orange-200" /> Significativo (2.5–3.5)</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-rose-300" /> Alto (≥3.5)</span>
          </div>
        </div>
      )}

      {drillDown && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/40 p-4" data-testid="drilldown-modal" onClick={() => setDrillDown(null)}>
          <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Drill-down</p>
                <h2 className="mt-1 font-heading text-2xl font-bold text-slate-950">ATECO {drillDown.ateco} · {drillDown.category}</h2>
                <p className="mt-1 text-sm text-slate-500">{drillDown.companies.length} aziende · {drillDown.assessments_total} valutazioni</p>
              </div>
              <button onClick={() => setDrillDown(null)} className="rounded-full p-1.5 hover:bg-slate-100" data-testid="drilldown-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6">
              {drillLoading && <p className="text-sm text-slate-500">{t("common.loading")}</p>}
              {drillDown.companies.length === 0 ? (
                <p className="text-sm text-slate-500">Nessuna azienda</p>
              ) : (
                <ul className="space-y-2" data-testid="drilldown-companies">
                  {drillDown.companies.map((c) => (
                    <li key={c.company_id} className="rounded-md border border-slate-200 p-3" data-testid={`drilldown-company-${c.company_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <a href={`/aziende/${c.company_id}`} className="font-semibold text-slate-800 hover:text-blue-700">{c.business_name}</a>
                        <span className="font-mono text-xs text-slate-500">{c.vat_number}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">basso {c.levels.basso}</span>
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">moderato {c.levels.moderato}</span>
                        <span className="rounded-full bg-orange-200 px-2 py-0.5 text-orange-900">significativo {c.levels.significativo}</span>
                        <span className="rounded-full bg-rose-300 px-2 py-0.5 text-rose-950">alto {c.levels.alto}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">totale {c.assessments_count}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {retention && retention.data?.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="analytics-retention-section">
          <h2 className="font-heading text-2xl font-bold text-slate-950">{t("analytics.retention_title")}</h2>
          <p className="mt-1 text-sm text-slate-500">Nuove aziende registrate e DVR completati per mese.</p>
          <div className="mt-5 grid grid-cols-12 gap-2">
            {retention.data.map((m) => {
              const maxNew = Math.max(...retention.data.map((x) => x.new), 1);
              return (
                <div key={m.month} className="flex flex-col items-center gap-1" data-testid={`retention-bar-${m.month}`}>
                  <div className="flex h-32 w-full flex-col-reverse rounded-md bg-slate-50 p-1">
                    <div className="rounded-sm bg-blue-500 transition-all" style={{ height: `${(m.new / maxNew) * 100}%` }} title={`${m.new} nuove`} />
                    <div className="rounded-sm bg-emerald-500/80 transition-all" style={{ height: `${(m.completed / maxNew) * 100}%` }} title={`${m.completed} completate`} />
                  </div>
                  <span className="text-[10px] text-slate-500">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-blue-500" /> Nuove aziende</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-500/80" /> DVR completati</span>
          </div>
        </section>
      )}
    </div>
  );
}
